import { Checkbox, FormControl, FormControlLabel } from '@mui/material';
import React, { useEffect } from 'react';

import { observer } from 'mobx-react';

import Box from '@mui/material/Box';

import PhoneInput from 'ui/PhoneInput';

import CustomTextInputField from 'components/SharedFormComponents/CustomTextInputField';
import { useStore } from 'utils/hooks/store';

import { Container, Header, InputWrapper, InputLabel } from './styles';

type UserDetailsStepProps = {
  initialState: { name: string | null; email: string | null; phone: string | null };
  onRemember: (rememberUserDetails: boolean) => void;
  rememberUserDetails: boolean;
};

const UserDetailsStep: React.FC<UserDetailsStepProps> = ({ initialState, onRemember, rememberUserDetails }) => {
  const {
    entitiesFormSubmission: {
      saveUserDetails,
      publicSubmissionUserDetails,
      userDetailsRequiredNotFilled,
      publicSubmissionUserDetailsFields,
      userDetailsErrors,
    },
  } = useStore();

  // Initialize store with values from localStorage when component mounts
  useEffect(() => {
    if (initialState.name) saveUserDetails('name', initialState.name);
    if (initialState.email) saveUserDetails('email', initialState.email);
    if (initialState.phone) saveUserDetails('phone', initialState.phone);
  }, [initialState]);

  const updateDetails = (key: 'name' | 'email' | 'phone', value: string): void => {
    saveUserDetails(key, value);
  };

  if (!publicSubmissionUserDetails) {
    return null;
  }

  const phoneNumber = publicSubmissionUserDetails?.phone || initialState.phone || '';

  const isGBPhone = phoneNumber.startsWith('+44');
  const startWith0 = (phoneNumber.trim().split(' ')[1] || [])[0] === '0';

  // display error message if phone number is not valid
  // for GB and start with 0 add extra note
  const helperTextForPhoneInput = userDetailsErrors.phone
    ? `Please check your phone number. ${
        isGBPhone && startWith0 ? ' It must include the correct number of digits and not start with a 0.' : ''
      }`
    : '';

  return (
    <Container>
      <Header>Your details</Header>
      {publicSubmissionUserDetailsFields?.name && (
        <InputWrapper>
          <InputLabel>
            Full Name
            <Box component="span" sx={{ marginLeft: '4px', color: 'red' }}>
              *
            </Box>
          </InputLabel>
          <CustomTextInputField
            description="Full Name"
            onChange={(value) => updateDetails('name', value as string)}
            value={publicSubmissionUserDetails?.name || initialState.name || ''}
            requiredNotFilled={userDetailsRequiredNotFilled.includes('name') || Boolean(userDetailsErrors.name)}
            errorMessage={(userDetailsErrors.name || []).join(', ')}
          />
        </InputWrapper>
      )}
      {publicSubmissionUserDetailsFields?.email && (
        <InputWrapper>
          <InputLabel>
            Email address
            <Box component="span" sx={{ marginLeft: '4px', color: 'red' }}>
              *
            </Box>
          </InputLabel>
          <CustomTextInputField
            description="Email address"
            onChange={(value) => updateDetails('email', value as string)}
            value={publicSubmissionUserDetails?.email || initialState.email || ''}
            requiredNotFilled={userDetailsRequiredNotFilled.includes('email')}
            errorMessage={(userDetailsErrors.email || []).join(', ')}
          />
        </InputWrapper>
      )}
      {publicSubmissionUserDetailsFields?.phone && (
        <InputWrapper>
          <InputLabel>
            Phone number
            <Box component="span" sx={{ marginLeft: '4px', color: 'red' }}>
              *
            </Box>
          </InputLabel>
          <PhoneInput
            variant="outlined"
            defaultCountry="gb"
            placeholder="Phone number"
            error={userDetailsRequiredNotFilled.includes('phone') || Boolean(userDetailsErrors.phone)}
            helperText={helperTextForPhoneInput}
            onChange={(val) => updateDetails('phone', val as string)}
            value={publicSubmissionUserDetails?.phone || initialState.phone || ''}
          />
        </InputWrapper>
      )}
      <FormControl>
        <FormControlLabel
          value="remember"
          label="Remember my details"
          control={<Checkbox checked={rememberUserDetails} />}
          onChange={() => onRemember(!rememberUserDetails)}
        />
      </FormControl>
    </Container>
  );
};

export default observer(UserDetailsStep);
